import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import InfluencerModal from './InfluencerModal';

const Influencers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchInfluencers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword
      };
      
      const response = await apiService.post('influencers', params);
      if (response && response.data) {
        setInfluencers(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load influencers');
      console.error('Influencers fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchInfluencers();
    }, searchKeyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchInfluencers]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const toggleInfluencerStatus = async (influencerId) => {
    try {
      const response = await apiService.get(`influencerStatus/${influencerId}`);
      if (response.status) {
        toast.success(response.message);
        setInfluencers(prevInfluencers => 
          prevInfluencers.map(influencer => 
            influencer._id === influencerId 
              ? { ...influencer, block: influencer.block === 1 ? 0 : 1 }
              : influencer
          )
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to update influencer status');
    }
  };

  const viewDetails = (influencer) => {
    setSelectedInfluencer(influencer);
    setShowModal(true);
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Influencers</h1>
              
              {/* Search */}
              <div className="relative w-64">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Type Name, Phone or Email"
                  value={searchKeyword}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email/Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verify Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Block Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
                      </div>
                    </td>
                  </tr>
                ) : influencers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      No influencers found
                    </td>
                  </tr>
                ) : (
                  influencers.map((influencer, index) => (
                    <tr key={influencer._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {((currentPage - 1) * recordsPerPage) + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-12 h-12">
                          {influencer.image ? (
                            <img 
                              src={`${process.env.REACT_APP_MEDIA_URL}${influencer.image}`} 
                              alt="Profile" 
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 rounded-full flex items-center justify-center">
                              <i className="bi bi-person text-gray-400 text-xl"></i>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-800">{influencer.first_name} {influencer.last_name}</span>
                          <span className="text-gray-500 text-xs">@{influencer.username || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          {influencer.email && (
                            <span className="mb-1 text-sm">
                              <i className="bi bi-envelope text-blue-500 mr-2"></i>
                              {influencer.email}
                            </span>
                          )}
                          {influencer.phone && (
                            <span className="text-sm">
                              <i className="bi bi-phone text-green-500 mr-2"></i>
                              {influencer.phone}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-gray-800 text-sm">{influencer.address || '--'}</span>
                          {(influencer.stateId?.name || influencer.countryId?.name) && (
                            <span className="text-gray-500 text-xs">
                              {influencer.stateId?.name}{influencer.stateId?.name && influencer.countryId?.name ? ', ' : ''}{influencer.countryId?.name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          influencer.verify === 'approved' ? 'bg-green-100 text-green-800' :
                          influencer.verify === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {influencer.verify === 'approved' ? 'Approved' :
                           influencer.verify === 'rejected' ? 'Rejected' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={influencer.block === 1}
                            onChange={() => toggleInfluencerStatus(influencer._id)}
                            className="sr-only"
                          />
                          <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            influencer.block === 1 ? 'bg-[#009ef7]' : 'bg-gray-200'
                          }`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              influencer.block === 1 ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </div>
                        </label>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(influencer.createdAt).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => viewDetails(influencer)}
                            className="px-3 py-1 text-xs bg-[#009ef7]/10 text-blue-700 rounded hover:bg-blue-200"
                            title="View Details"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <button
                            onClick={() => navigate(`/influencer/edit/${influencer._id}`)}
                            className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                            title="Edit Profile"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} results
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedInfluencer && (
        <InfluencerModal 
          influencer={selectedInfluencer} 
          onClose={() => setShowModal(false)} 
        />
      )}
    </div>
  );
};

export default Influencers;